// src/pages/outsourcing_management/requisitions/RequisitionSalesOrderPartPicker.tsx
//
// EE-207 Phase 3 (#11) - Sales Order + Part (order item) picker, required
// once Purpose=Sales Order (options.purposes[].requiresSalesOrderAndPart).
//
// Reuse note: the codebase has no existing Autocomplete-based order/order-item
// selector - the only prior art is RequisitionComponent.tsx's order search,
// which is a bespoke debounced text-input + manual dropdown wired into a
// multi-item production-task selection table (InquiryItemsTable),
// not a single order+single part picker, and not MUI Autocomplete-based. It
// isn't a fit to reuse directly, so this is a small new component - but it
// reuses the EXISTING network calls (apiGetOrders for search, apiGetOrder for
// prefill hydration) rather than adding new endpoints.
import { FunctionComponent, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { apiGetOrder, apiGetOrders } from "util/network/Orders";
import {
  ManufacturedProductDetailsType,
  ProductTypeEnum,
} from "types/products/ProductCommandsType";
import { AddressType } from "types/AddressTypes";
import RequisitionPartPreview from "./RequisitionPartPreview";
import PartsSelectAllPaper, {
  PartsSelectAllContext,
} from "./PartsSelectAllPaper";
import { finishLabel, isPartEligible } from "./partEligibility";

// id is the order item id (what the requisition stores); productId + name are
// display only - admin renders parts as "#productId modelName" everywhere
// (see ManufacturedCartItemView / OrderConfigRow). modelId is what the preview
// image is keyed on and is absent for non-manufactured (custom) parts.
type PickerOrderItem = {
  id: number;
  productId: number;
  name: string;
  modelId?: number;
  /** Undefined when the part records no finish - see finishLabel. */
  finish?: string;
  /** Decides whether this part belongs under the chosen requisition type. */
  productType?: ProductTypeEnum;
  /** How many were ordered - the item line starts from this. */
  quantity?: number;
};
// Customer as the order rows show it: company on top, contact name below
// (see OrderRow / QuoteRow).
type PickerOrderCustomer = { company: string; name: string; email: string };
// EE-233 - the order LIST endpoint already carries the customer (SimpleOrderVM
// serializes firstName/lastName/email/billingAddress), so options can show who
// the order belongs to without a per-option apiGetOrder.
type PickerOrderOption = {
  id: number;
  orderNo: string;
  customer?: PickerOrderCustomer;
};
// The order LIST endpoint (SimpleOrderVM) serializes items as a list of item
// IDs, not objects - only the single-order endpoint (OrderVM) returns full
// order items. So parts always come from the apiGetOrder hydration below.
type PickerOrder = PickerOrderOption & {
  items: PickerOrderItem[];
  hydrated: boolean;
};

const DETAIL_SLOT_HEIGHT = 64;
// The customer slot appears the moment an order is picked and keeps the same
// height whether it holds a skeleton or the customer, so nothing below the
// picker moves while apiGetOrder loads.
const detailSlotSx = {
  mt: 1.5,
  minHeight: DETAIL_SLOT_HEIGHT,
  // 14px lines the slot up with the text inside the outlined input above it.
  pl: "14px",
  display: "flex",
  alignItems: "center",
} as const;

// Same shape in OrderVM and SimpleOrderVM, so one mapper serves both the search
// options and the hydrated order.
const toCustomer = (order: {
  firstName?: string;
  lastName?: string;
  email?: string;
  billingAddress?: AddressType;
}): PickerOrderCustomer => ({
  company: order.billingAddress?.company ?? "",
  name: `${order.firstName ?? ""} ${order.lastName ?? ""}`.trim(),
  email: order.email ?? order.billingAddress?.email ?? "",
});

// Company first, contact name as the fallback - matches how OrderRow labels a
// customer when the order has no company on its billing address.
const customerPrimary = (customer?: PickerOrderCustomer) =>
  customer?.company || customer?.name || "";
const customerSecondary = (customer?: PickerOrderCustomer) =>
  [customer?.company ? customer.name : "", customer?.email]
    .filter(Boolean)
    .join(" - ");

// Long company names / part names truncate instead of wrapping into a second
// line and pushing the slot taller. Kept off the slot itself: the preview frame
// is a Box (a div too) and must not be stretched by a `& > div` rule.
const detailTextSx = { flex: 1, minWidth: 0 } as const;

/** What the parent needs to know about a picked part to build its line. */
export type PickedPart = {
  orderItemId?: number;
  quoteItemId?: number;
  productId?: number;
  name?: string;
  quantity?: number;
};

type PropsType = {
  token: string;
  salesOrderId?: number;
  /** The picked parts, in the order they were picked - one line each. */
  orderItemIds: number[];
  onChange: (change: { salesOrderId?: number; orderItemIds: number[] }) => void;
  salesOrderError?: string;
  orderItemError?: string;
  disabled?: boolean;
  /** Finishing requisitions only: mark and refuse parts with no finish. */
  requireFinish?: boolean;
  /** The requisition's type code - the part list is narrowed to it. */
  typeCode?: string;
  /**
   * The picked parts once the order is hydrated, for building their lines.
   * Fires on hydration too, so a requisition opened from an Outsource button
   * - where the part is already chosen and this picker is never touched -
   * gets its line as well. Empty until the order has loaded: the parent
   * must not read an empty list as "nothing picked".
   */
  onPartsResolved?: (parts: PickedPart[]) => void;
};

const RequisitionSalesOrderPartPicker: FunctionComponent<PropsType> = ({
  token,
  salesOrderId,
  orderItemIds,
  onChange,
  salesOrderError,
  orderItemError,
  disabled,
  requireFinish,
  typeCode,
  onPartsResolved,
}) => {
  const [selectedOrder, setSelectedOrder] = useState<PickerOrder | null>(null);
  const [orderInputValue, setOrderInputValue] = useState("");
  const [orderOptions, setOrderOptions] = useState<PickerOrderOption[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [hydrating, setHydrating] = useState(false);

  // Hydrate the selected order (incl. its parts) whenever salesOrderId changes -
  // covers picking an order here, the TaskTableRow prefill entry point, and an
  // eventual edit-Draft prefill.
  useEffect(() => {
    if (!salesOrderId) {
      setSelectedOrder(null);
      return;
    }
    if (selectedOrder?.id === salesOrderId && selectedOrder.hydrated) return;

    let cancelled = false;
    setHydrating(true);
    apiGetOrder(token, salesOrderId)
      .then((order) => {
        if (cancelled) return;
        setSelectedOrder({
          id: order.id,
          orderNo: order.orderNo,
          items: (order.items ?? []).map((item) => ({
            id: item.id,
            productId: item.productId,
            name: item.name,
            modelId: (item.product?.details as ManufacturedProductDetailsType)
              ?.modelId,
            finish: finishLabel(
              item.product?.details as ManufacturedProductDetailsType
            ),
            productType: item.product?.productType,
            quantity: item.quantity,
          })),
          customer: toCustomer(order),
          hydrated: true,
        });
      })
      .catch((err) => {
        // Was swallowed silently, which turned any failure in here - including
        // a throw while mapping the items - into an empty part list with no
        // hint as to why. The selection still round-trips via onChange.
        console.error("Failed to load parts for the selected %s", "order", err);
      })
      .finally(() => {
        if (!cancelled) setHydrating(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesOrderId, token]);

  // Debounced order search-as-you-type.
  useEffect(() => {
    const term = orderInputValue.trim();
    if (!term) {
      setOrderOptions([]);
      return;
    }
    setLoadingOrders(true);
    const handle = setTimeout(() => {
      apiGetOrders(token, 1, 20, "id", "desc", undefined, term)
        .then((res) => {
          setOrderOptions(
            (res.items ?? []).map((order) => ({
              id: order.id,
              orderNo: order.orderNo,
              customer: toCustomer(order),
            }))
          );
        })
        .catch(() => setOrderOptions([]))
        .finally(() => setLoadingOrders(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [orderInputValue, token]);

  // A requisition asks for one kind of work, so only the parts that kind of
  // work applies to are offered - a sheet part on the order has no business
  // appearing under a CNC requisition. Only manufactured parts (CNC, sheet, 3D
  // print) are ever offered - see partEligibility - and a finishing
  // requisition takes any of them with a finish on it.
  const parts = (selectedOrder?.items ?? []).filter((item) =>
    isPartEligible(item.productType, typeCode)
  );

  // In pick order, so the lines the parent builds from them keep that order.
  // An id the hydrated order does not know is dropped silently: it was the
  // previous order's, and the parent clears the ids on an order change.
  const selectedParts = orderItemIds
    .map((id) => selectedOrder?.items.find((item) => item.id === id))
    .filter((item): item is PickerOrderItem => !!item);

  // What "Select all" picks: every offered part a click could pick by hand,
  // so a finishing requisition skips the parts it refuses anyway.
  const selectable = parts.filter((item) => !requireFinish || !!item.finish);
  const allSelected =
    selectable.length > 0 &&
    selectable.every((item) => orderItemIds.includes(item.id));
  // The "Select all" row at the top of the dropdown - see PartsSelectAllPaper.
  const selectAll = {
    total: selectable.length,
    selected: selectable.filter((item) => orderItemIds.includes(item.id)).length,
    onToggle: () =>
      onChange({
        salesOrderId: selectedOrder?.id,
        orderItemIds: allSelected ? [] : selectable.map((item) => item.id),
      }),
  };

  // A type change can leave picks behind that the new type would never have
  // offered - a sheet part under a requisition switched to CNC, a part with
  // no finish once it became a finishing requisition. Drop them, the same
  // way the list refuses them fresh; the rest of the picks stay.
  const ineligibleIds = selectedParts
    .filter((item) => !selectable.some((s) => s.id === item.id))
    .map((item) => item.id);
  const ineligibleKey = ineligibleIds.join(",");
  useEffect(() => {
    // Not while loading: the picks may belong to a record not resolved yet.
    if (hydrating || !selectedOrder || ineligibleIds.length === 0) return;
    onChange({
      salesOrderId: selectedOrder.id,
      orderItemIds: orderItemIds.filter((id) => !ineligibleIds.includes(id)),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ineligibleKey, hydrating]);

  // Keyed on a string of the resolved parts, not the array: the array is a
  // fresh object every render and would re-fire this on each one.
  const resolvedKey = selectedParts
    .map((item) => `${item.id}:${item.quantity ?? ""}`)
    .join(",");
  useEffect(() => {
    onPartsResolved?.(
      selectedParts.map((item) => ({
        orderItemId: item.id,
        productId: item.productId,
        name: item.name,
        quantity: item.quantity,
      }))
    );
    // The callback is deliberately out of the deps: parents pass an inline
    // function, and including it would re-fire on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedKey]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          size="small"
          disabled={disabled}
          options={orderOptions}
          loading={loadingOrders || hydrating}
          value={selectedOrder}
          filterOptions={(options) => options} // server-side filtering already applied
          getOptionLabel={(order) => order.orderNo ?? `#${order.id}`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onInputChange={(_, value) => setOrderInputValue(value)}
          // EE-233 - the backend search already matches customer name, e-mail
          // and billing company (OrderController), so the option has to show
          // the customer or a name search returns rows that look unrelated.
          renderOption={(props, order) => (
            <li {...props} key={order.id}>
              <Box sx={detailTextSx}>
                <Typography variant="body2" noWrap>
                  {order.orderNo ?? `#${order.id}`}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  noWrap
                >
                  {[
                    customerPrimary(order.customer),
                    customerSecondary(order.customer),
                  ]
                    .filter(Boolean)
                    .join(" - ") || "No customer details on this order"}
                </Typography>
              </Box>
            </li>
          )}
          onChange={(_, order) => {
            // Parts stay empty until the effect above hydrates them from
            // apiGetOrder; the label renders off orderNo in the meantime.
            setSelectedOrder(
              order ? { ...order, items: [], hydrated: false } : null
            );
            onChange({ salesOrderId: order?.id, orderItemIds: [] });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Sales Order"
              required
              error={!!salesOrderError}
              helperText={
                salesOrderError ?? "Search by order number, customer or company"
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingOrders || hydrating ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {selectedOrder && (
          <Box sx={detailSlotSx}>
            {selectedOrder.customer ? (
              <Box sx={detailTextSx}>
                <Typography variant="body2" noWrap>
                  {customerPrimary(selectedOrder.customer) || "-"}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  noWrap
                >
                  {customerSecondary(selectedOrder.customer) ||
                    "No customer details on this order"}
                </Typography>
              </Box>
            ) : (
              <Box sx={detailTextSx}>
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width="50%" />
              </Box>
            )}
          </Box>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <PartsSelectAllContext.Provider value={selectAll}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            PaperComponent={PartsSelectAllPaper}
            size="small"
            disabled={disabled || !selectedOrder || hydrating}
            loading={hydrating}
            options={parts}
            value={selectedParts}
            getOptionLabel={(item) =>
              item.name ? `#${item.productId} ${item.name}` : `#${item.productId}`
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, items) =>
              onChange({
                salesOrderId: selectedOrder?.id,
                orderItemIds: items.map((item) => item.id),
              })
            }
            getOptionDisabled={(item) => !!requireFinish && !item.finish}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((item, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={item.id}
                  size="small"
                  label={
                    item.name ? `#${item.productId} ${item.name}` : `#${item.productId}`
                  }
                />
              ))
            }
            renderOption={(props, item, { selected }) => {
              const blocked = !!requireFinish && !item.finish;
              return (
                <li {...props} key={item.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Checkbox
                      size="small"
                      checked={selected}
                      tabIndex={-1}
                      sx={{ p: 0 }}
                    />
                    <RequisitionPartPreview
                      token={token}
                      modelId={item.modelId}
                      size={40}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <span>
                        {item.name
                          ? `#${item.productId} ${item.name}`
                          : `#${item.productId}`}
                      </span>
                      {/* Only shown for a finishing requisition: everywhere
                          else the finish is not what is being ordered, and
                          this is noise. */}
                      {requireFinish && (
                        <Typography
                          variant="caption"
                          display="block"
                          color={blocked ? "error" : "text.secondary"}
                        >
                          {item.finish ?? "No finish recorded - cannot be finished"}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Parts"
                required={selectedParts.length === 0}
                error={!!orderItemError}
                helperText={
                  orderItemError ??
                  (hydrating
                    ? "Loading parts..."
                    : !selectedOrder
                    ? "Select a Sales Order first"
                    : parts.length
                    ? "Select one or more parts from this order - one line each"
                    // Says why the list is empty. Without this a type with no
                    // matching parts looks identical to a failed load.
                    : `No parts on this order match this requisition type`)
                }
              />
            )}
          />
        </PartsSelectAllContext.Provider>
      </Grid>
    </Grid>
  );
};

export default RequisitionSalesOrderPartPicker;
